import React from "react";
import { createRoot } from "react-dom/client";
import { applyTheme } from "@cloudscape-design/components/theming";
import "./index.css";
import App from "./App";
import reportWebVitals from "./reportWebVitals";
import LoginPage from "./routes/LoginPage";
import AuthLoading from "./routes/AuthLoading";
import {
  parseAuthQueryString,
  getToken,
  handleCode,
} from "./api/auth";

// Align Cloudscape controls with the CloudZA design system so they are
// indistinguishable from the Tailwind-styled parts of the app.
applyTheme({
  theme: {
    tokens: {
      fontFamilyBase: "'Figtree', sans-serif",

      // Primary buttons — ink pill, violet on hover
      colorBackgroundButtonPrimaryDefault: { light: "#17151f", dark: "#7c3aed" },
      colorBackgroundButtonPrimaryHover: { light: "#7c3aed", dark: "#8b5cf6" },
      colorBackgroundButtonPrimaryActive: "#6d28d9",
      colorBackgroundButtonPrimaryDisabled: "#c4b5fd",
      colorTextButtonPrimaryDefault: "#ffffff",
      colorTextButtonPrimaryHover: "#ffffff",
      colorTextButtonPrimaryActive: "#ffffff",

      // Normal/secondary buttons — neutral outline, never Cloudscape blue
      colorTextButtonNormalDefault: { light: "#17151f", dark: "#e8e6ef" },
      colorTextButtonNormalHover: { light: "#17151f", dark: "#ffffff" },
      colorTextButtonNormalActive: { light: "#17151f", dark: "#ffffff" },
      colorBorderButtonNormalDefault: { light: "#dedce8", dark: "#3a3450" },
      colorBorderButtonNormalHover: { light: "#17151f", dark: "#4a4363" },
      colorBorderButtonNormalActive: { light: "#17151f", dark: "#4a4363" },
      colorBackgroundButtonNormalDefault: { light: "#ffffff", dark: "#201d2b" },
      colorBackgroundButtonNormalHover: { light: "#f6f5fa", dark: "#2b2637" },
      colorBackgroundButtonNormalActive: { light: "#ede9fe", dark: "#2e2748" },

      // Links and interactive accents
      colorTextLinkDefault: { light: "#7c3aed", dark: "#a78bfa" },
      colorTextLinkHover: { light: "#5b21b6", dark: "#c9b8fc" },
      colorTextAccent: { light: "#7c3aed", dark: "#a78bfa" },
      colorBorderItemFocused: "#7c3aed",
      colorBackgroundControlChecked: "#7c3aed",

      // Shapes — pills for buttons, soft radius for inputs and surfaces
      borderRadiusButton: "9999px",
      borderRadiusInput: "10px",
      borderRadiusContainer: "14px",
      borderRadiusBadge: "9999px",
    },
  },
});

const root = createRoot(document.getElementById("root"));

const renderApp = () => {
  root.render(
    <React.StrictMode>
      <App />
    </React.StrictMode>
  );
};

(async () => {
  // Token check and code exchange both hit the network, so show the branded
  // waiting state rather than leaving a blank page for their duration.
  root.render(<AuthLoading />);

  try {
    const tokenData = parseAuthQueryString();
    if (tokenData) await handleCode(tokenData);
    const token = await getToken();
    if (!token) {
      root.render(<LoginPage />);
      return;
    }
    renderApp();
  } catch (e) {
    console.error(e);
    // Surface the reason instead of dropping the user on an unexplained
    // sign-in card — a bad pool config looked exactly like a normal sign-out.
    root.render(<LoginPage error={e?.message} />);
  }
})();

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();